/**
 * The Orders print packet: what the Print dialog asks for and what the kitchen
 * page hands the packet. Matt's V2 design (canvas "Print Packet Mockups — Thu
 * 10/15", Oct 16), as Abner updated it on Oct 18 (Figma "Updated proposal")
 * and Matt fine-tuned it on Oct 20 (canvas "Print packet changelog"): per
 * delivery day, the cover (the date and the stops), then three sections in
 * packet order. Ordering: the shopping list. Production: the production
 * summary & pack list, the milk page, the recipes with their cover. Delivery:
 * the driver's route, then per stop in route order its summary: the kitchen
 * copy the driver brings back, on top, then the school copy that stays with
 * the school.
 *
 * Pure. The shopping-list page prints its own tabs through `PrintRoot`
 * (`print-plan.ts`); this is the Orders packet only.
 */

import type { DeliveryRouteResponse } from '@/api/delivery-route'
import { formatShortDay, formatShortDayRange } from '@/lib/chefs/format'
import type { PrintDay, PrintPreliminary } from '@/lib/chefs/print-plan'
import type { MeasurementDisplay } from '@/lib/chefs/recipe-quantities'

/** The dialog's three sections and its one option (Matt, Oct 20: "3 sections instead of 8 options"). The cover always prints. */
export interface PacketSections {
  /** 1 · Ordering, what to buy: the shopping list. One list for the whole range on a multi-day print (Abner, 164:858). */
  ordering: boolean
  /** 2 · Production, what to make, prep and stage: the production summary & pack list, the milk page, the recipes. */
  production: boolean
  /** 3 · Delivery, what goes on the truck: the driver route, then each stop's summary (the school copy). */
  delivery: boolean
  /**
   * A kitchen copy of every stop summary, on top of its school copy: the school signs it, the driver brings it back.
   * It holds the stop's only sign-off (Matt, Oct 20). Needs `delivery`.
   */
  kitchenCopy: boolean
}

export const PACKET_SECTION_DEFAULTS: PacketSections = {
  ordering: true,
  production: true,
  delivery: true,
  kitchenCopy: true,
}

/** The sections that add pages; the kitchen copy is an option of the delivery section. */
const PAGE_SECTIONS = ['ordering', 'production', 'delivery'] as const

/** True when at least one section beyond the cover is ticked. */
export function anyPacketSection(sections: PacketSections): boolean {
  return PAGE_SECTIONS.some((key) => sections[key])
}

export interface PacketTarget {
  sections: PacketSections
  /** The picked delivery days that carry food, chronological. Always at least one. */
  days: PrintDay[]
  kitchenName: string
  /**
   * The delivery route for these days (`/api/kitchen-orders/delivery-route`):
   * the stop order the letters follow, addresses, windows, contacts and each
   * stop's Google Maps link. Null when it could not load: the stops keep the
   * Orders screen's order and the pages print without route facts.
   */
  route: DeliveryRouteResponse | null
  /** The kitchen's Standard | Technical measurement setting, for the recipe amounts. */
  measurement: MeasurementDisplay
  /**
   * The origin the route page opens on ("https://app.ordo.com"): the route's one code is this origin and the day's
   * signed `link.path`. Without it the code opens the whole route in Google Maps.
   */
  routeLinkOrigin?: string
  /**
   * Set while a school on these days can still change its order (PROD-1452):
   * the lock labels per page scope (`printPreliminary`). Only the pages whose
   * counts can still change carry the INCOMPLETE ORDER label and watermark
   * (`sectionLocksAtLabel`).
   */
  preliminary?: PrintPreliminary
}

/** "Thu 10/15", or "Mon 10/12 – Fri 10/16" across days. */
export function packetRangeLabel(days: ReadonlyArray<Pick<PrintDay, 'date'>>): string {
  const first = days[0]?.date
  const last = days[days.length - 1]?.date
  if (!first || !last) {
    return ''
  }
  return first.toDateString() === last.toDateString() ? formatShortDay(first) : formatShortDayRange(first, last)
}

/**
 * The browser print and PDF file name: "Ordo_Orders_Thu 10/15", with
 * "_INCOMPLETE" while the counts can still change, so a saved PDF never passes
 * for the final copy.
 */
export function packetDocumentTitle(target: Pick<PacketTarget, 'days' | 'preliminary'>): string {
  return `Ordo_Orders_${packetRangeLabel(target.days)}${target.preliminary ? '_INCOMPLETE' : ''}`
}
